/**
 * Pure utilities for normalising Pyodide trace frames against canvas and
 * stdout history.
 *
 * Extracted from `worker.ts` into its own module so the logic can be unit-tested
 * without pulling in the Web Worker globals (`self`, Pyodide network imports,
 * etc.) that make `worker.ts` un-importable in a Node/Vitest environment.
 *
 * The two halves are exported separately so the FINISHED path (which has
 * canvasHistory in hand) and the SEND_INTERRUPT_STEP_DATA path (which posts
 * synchronously from inside `get_controls_info` and so cannot await the
 * graphics-worker round-trip) can compose them differently:
 *
 * - FINISHED: worker calls `zipTraceDataAndGraphics` (= normalise + merge).
 * - SEND_INTERRUPT_STEP_DATA: worker calls `normalizeOutputHistory`, posts
 *   the already-normalised `traceList` and `string[]` `outputList`. Main
 *   thread fetches canvasHistory from the graphics worker and finishes
 *   with `mergeCanvasIntoTrace`.
 *
 * Keeping both paths funneled through these shared helpers prevents the
 * format drift that produced #2711 (a duplicate copy of this logic in
 * `WorkerApi.ts` only populated `newOutputHistory` when
 * `stdoutOffset > 0`, dropping all chunks for graphics programs after Stop).
 */

import {StepVariables} from '../types/general.js';
import {
  type CanvasHistoryMap,
  type OutputChunkMap,
  type RawTraceFrame,
  type ZippedTraceFrame,
} from './types.js';

/**
 * Result of `normalizeOutputHistory` — a `traceList` whose
 * `output_pointer`s are aligned with index 0 of `outputList`, and the
 * dense ordered `outputList` that a fresh `CappedMap` should be repopulated
 * with on the receiving side.
 */
interface NormalizedOutput {
  /** Trace frames with `output_pointer` shifted into the canonical
   *  index-0-based scheme. Same identity as the input when no shift was
   *  required (common case); otherwise a fresh array of fresh frame objects. */
  traceList: RawTraceFrame[];
  /** Dense ordered list of stdout chunks ready to be transferred over
   *  postMessage and rehydrated into a numeric-keyed `CappedMap`. */
  outputList: string[];
}

/**
 * Normalises a worker-side `outputHistory` (CappedMap) and the trace
 * frames that reference it into a postMessage-friendly `string[]` plus
 * a `traceList` whose `output_pointer`s are aligned with index 0 of
 * that array.
 *
 * Whether a shift is applied is decided by *actual CappedMap eviction*,
 * not by the first frame's `output_pointer`. The first surviving (post
 * setup-splice) trace frame can fire after the program has already
 * emitted real, user-visible stdout — those chunks are still in
 * `outputHistory` and must be carried, not stripped. The eviction signal
 * is `outputHistory`'s earliest retained key: `> 0` iff the CappedMap
 * has dropped older entries.
 *
 * Index correspondence (closed interval, both bounds inclusive):
 * - Eviction case (`earliestKey > 0`): chunks before `earliestKey` are
 *   gone from memory. Frame output_pointers are shifted by `-earliestKey`
 *   so the new frame[0] pointer maps to index 0 of `outputList`.
 *   `outputList[j]` corresponds to `outputHistory.get(earliestKey + j)`,
 *   for `j ∈ [0, outputHistoryMax - earliestKey]`.
 * - Common case (no eviction, `earliestKey === 0`): frame output_pointers
 *   are unchanged. `outputList[j]` corresponds to `outputHistory.get(j)`,
 *   for `j ∈ [0, outputHistoryMax]`. This holds even when the first
 *   frame's `output_pointer > 0` — those leading chunks are real output
 *   from setup-spliced lines (e.g. `print(...)` ahead of `import numpy`)
 *   and must be preserved. Stripping them by gating on
 *   `firstFrameOutputPointer > 0` was the bug behind #2709.
 *
 * Critical invariant: `outputList` carries every chunk from `outputHistory`
 * whenever `outputHistory` is non-empty. The round-trip
 * `worker posts → main saves → SETUP.preloadedStepData.outputList`
 * depends on this; an empty `outputList` causes `self.outputHistory` to be
 * restored empty after a Stop-induced re-init, making every slider scrub
 * render empty console output (issues #2692, #2711).
 *
 * @param params.traceList - Raw Pyodide tracer frames. May be empty; in
 *   that case the result is `{traceList: [], outputList: []}`.
 * @param params.outputHistory - CappedMap (or any Map-like with `.size`,
 *   `.keys()`, and `.get()`) from chunk index to stdout string chunk.
 * @returns Normalised trace list and dense stdout list.
 */
export const normalizeOutputHistory = ({
  traceList,
  outputHistory,
}: {
  traceList: RawTraceFrame[];
  outputHistory: OutputChunkMap;
}): NormalizedOutput => {
  if (traceList.length === 0) {
    return {traceList: [], outputList: []};
  }

  // Eviction signal: a non-empty CappedMap whose earliest retained key
  // is non-zero implies oldest chunks have been dropped. Use this — not
  // `firstFrameOutputPointer > 0` — to decide whether to shift, so that
  // pre-trace user output (chunks written before the first surviving
  // user frame, but still resident in the map) is preserved.
  const earliestKey: number = outputHistory.keys().next().value ?? 0;

  // `stdoutOffset` is the starting index for chunk copying. A value of
  // `-1` is a sentinel meaning "skip the population block entirely" —
  // used only when there is no output to carry.
  let stdoutOffset: number;
  if (outputHistory.size === 0) {
    stdoutOffset = -1;
  } else {
    stdoutOffset = earliestKey;
  }

  const outputList: string[] = [];
  if (stdoutOffset >= 0) {
    if (stdoutOffset > 0) {
      // Eviction case: shift every frame's `output_pointer` by
      // `-stdoutOffset` so the new frame[0] pointer maps to `outputList[0]`.
      // In the no-eviction common case (stdoutOffset === 0) frame pointers
      // are already aligned with chunk indices 0..N and no shift is needed.
      traceList = traceList.map((traceItem) => ({
        ...traceItem,
        [StepVariables.OUTPUT_POINTER]: traceItem[StepVariables.OUTPUT_POINTER] - stdoutOffset,
      }));
    }

    const outputHistoryMax = Math.max(...outputHistory.keys());
    for (let i = stdoutOffset; i <= outputHistoryMax; i++) {
      // Preserve array layout: `outputList[j]` must correspond to
      // `outputHistory.get(stdoutOffset + j)` so frames' (possibly
      // shifted) `output_pointer`s index correctly. Substitute an empty
      // string for any missing slot; `buildVisibleStep` tolerates empty
      // contributions in its concatenation loop.
      outputList.push(outputHistory.get(i) ?? '');
    }
  }

  return {traceList, outputList};
};

/**
 * Merges canvas snapshots into a trace list. Canvas data is keyed by
 * `canvas_call_pointer`; frames whose pointer is missing from
 * `canvasHistory` fall back to the earliest available `pre` snapshot
 * (so e.g. the first frame still gets a sensible initial canvas).
 *
 * The output of this function is what drives the replay slider's canvas
 * panel. It is independent of stdout normalisation, so it can be applied
 * either on the worker (FINISHED, sync) or on the main thread (post-
 * interrupt, after fetching canvas history from the graphics worker).
 *
 * @param params.traceList - Trace frames (typically already passed
 *   through `normalizeOutputHistory`). Each frame must carry a
 *   `canvas_call_pointer`. Empty inputs return `[]`.
 * @param params.canvasHistory - Map from `canvas_call_pointer` to
 *   `{pre, post}` snapshot pairs.
 * @returns A new array of frames with `canvas_data`/`canvasData`
 *   attached. Frame identity is not preserved.
 */
export const mergeCanvasIntoTrace = ({
  traceList,
  canvasHistory,
}: {
  traceList: RawTraceFrame[];
  canvasHistory: CanvasHistoryMap;
}): ZippedTraceFrame[] => {
  return traceList.map((traceItem) => {
    if (!canvasHistory.has(traceItem[StepVariables.CANVAS_CALL_POINTER])) {
      const earliestCanvasHistoryValue = canvasHistory.values().next().value;
      return {
        ...traceItem,
        canvasData: earliestCanvasHistoryValue?.pre,
      };
    }
    const canvasData = canvasHistory.get(traceItem[StepVariables.CANVAS_CALL_POINTER]);
    return {
      ...traceItem,
      canvas_data: canvasData?.post,
    };
  });
};

/**
 * FINISHED-path convenience wrapper that runs both halves in sequence.
 * Used by the worker when it has canvasHistory in hand at end-of-run.
 *
 * @param traceList - Raw Pyodide tracer frames.
 * @param canvasHistory - Map from `canvas_call_pointer` to snapshot pairs.
 * @param outputHistory - CappedMap (or Map-like) of stdout chunks.
 * @returns A two-element tuple `[zippedTraceList, outputList]`.
 */
export const zipTraceDataAndGraphics = (
  traceList: RawTraceFrame[],
  canvasHistory: CanvasHistoryMap,
  outputHistory: OutputChunkMap,
): [ZippedTraceFrame[], string[]] => {
  const {traceList: normalised, outputList} = normalizeOutputHistory({
    traceList,
    outputHistory,
  });
  if (normalised.length === 0) {
    return [[], outputList];
  }
  return [mergeCanvasIntoTrace({traceList: normalised, canvasHistory}), outputList];
};
